import React, { useMemo, useState } from "react";
import { Chat } from "stream-chat-react";
import "stream-chat-react/dist/css/v2/index.css";
import { Channel as ChannelType } from "stream-chat/dist/types/channel";
import { AscDesc, DefaultGenerics } from "stream-chat/dist/types/types";
import { AiOutlineWarning } from "react-icons/ai";
import "./AIChatScreen.css";
import { AIWebChatsMessages } from "../../components/aiChatScreen/web/aiWebChatMessages/AIWebChatsMessages";
import { ChatsList } from "../../components/aiChatScreen/chatsList/ChatsList";
import { StaticBackdropModal } from "../../components/modal/staticBackdropModal/StaticBackdropModal";
import { useWebChat } from "../../context/webChatContext";
import { useChatClient } from "../../hooks/useChatClient";
import { Strings } from "../../utils/strings";
import LoadingIndicator from "../../components/loadingIndicator/LoadingIndicator";
import { isMobile } from "react-device-detect";
import { AIMobileChatsMessages } from "../../components/aiChatScreen/mobile/aiMobileChatsMessages/AIMobileChatsMessages";
import { TopBar } from "../../components/topBar/TopBar";
import { useNavigate } from "react-router-dom";

const AIChatScreen: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { chatClient } = useChatClient();
  const [show, setShow] = useState(false);
  const [modalData, setModalData] = useState<{
    title: string;
    children: React.ReactNode | string;
    btnText?: string;
    btnVariant?: string;
    onClose: () => void;
    onAccept?: () => void;
    headerIcon?: React.ReactNode;
  }>();

  const { onLogOut, userModel, setChannel, channel, handleCreateChannel } =
    useWebChat();

  const filters = useMemo(
    () => ({
      members: { $in: [chatClient.user?.id ?? userModel?.guid ?? ""] },
      type: "system-jarvis",
    }),
    [chatClient.user?.id, userModel?.guid]
  );
  const options = { presence: true, state: true, limit: 10 };
  const sort = { last_message_at: -1 as AscDesc };

  const handleChannelClick = (newChannel: ChannelType<DefaultGenerics>) => {
    setChannel(newChannel);
  };

  const handleBackClick = () => {
    setChannel(undefined);
  };

  const navigate = useNavigate();

  const handleClose = async (isLogout: boolean = false) => {
    setShow(false);
    setModalData(undefined);
  };

  const handleLogout = () => {
    setModalData({
      title: Strings.LOGOUT,
      children: Strings.LOGOUT_WARNING,
      btnText: Strings.LOGOUT,
      btnVariant: "outline-danger",
      headerIcon: (
        <AiOutlineWarning
          style={{
            marginRight: "0.5em",
            color: "red",
            alignSelf: "center",
          }}
        />
      ),
      onClose: () => {
        handleClose(true);
        onLogOut();
      },
    });
    setShow(true);
  };

  return (
    <div className="chat-screen-container" style={channel ? {} : { gap: 15 }}>
      <Chat client={chatClient}>
        <TopBar
          onBackPress={channel ? handleBackClick : () => navigate(-1)}
          title={channel ? Strings.CHAT_ULTRA : ""}
          onLogoutPress={handleLogout}
        />
        {channel ? (
          isMobile ? (
            <AIMobileChatsMessages channel={channel} />
          ) : (
            <AIWebChatsMessages channel={channel} />
          )
        ) : (
          <ChatsList
            loading={loading}
            handleChannelClick={handleChannelClick}
            handleCreateChannel={async () => {
              setLoading(true);
              await handleCreateChannel();
              setLoading(false);
            }}
            filters={filters}
            sort={sort}
            options={options}
            setLoading={setLoading}
          />
        )}
      </Chat>
      <StaticBackdropModal
        show={show}
        handleOpen={modalData?.onAccept ?? modalData?.onClose ?? handleClose}
        handleClose={handleClose}
        title={modalData?.title}
        children={modalData?.children}
        btnText={modalData?.btnText}
        headerIcon={modalData?.headerIcon}
      />
      {(loading || !chatClient.userID) && <LoadingIndicator loading />}
    </div>
  );
};

export default AIChatScreen;
